import React, { useEffect, useState } from 'react'
import {
  Table, Tag, Card, Row, Col, Statistic, Select,
  Tooltip, Typography, Space, Badge, Descriptions
} from 'antd'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  StopOutlined,
  FileProtectOutlined,
} from '@ant-design/icons'
import useApi from '../../Utils/useApi'

const { Text } = Typography
const { Option } = Select

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  pendiente:   { label: 'Recibida',     color: 'orange',  icon: <ClockCircleOutlined /> },
  enviada_wms: { label: 'En EIT',       color: 'blue',    icon: <SyncOutlined spin /> },
  procesada:   { label: 'Procesada',    color: 'green',   icon: <CheckCircleOutlined /> },
  error_wms:   { label: 'Error WMS',    color: 'red',     icon: <CloseCircleOutlined /> },
  facturada:   { label: 'Facturada',    color: 'purple',  icon: <FileProtectOutlined /> },
  anulada:     { label: 'Anulada',      color: 'default', icon: <StopOutlined /> },
}

function StatusTag({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'default', icon: null }
  return (
    <Tag color={cfg.color} icon={cfg.icon} style={{ margin: 0 }}>
      {cfg.label}
    </Tag>
  )
}

// ── Resumen superior ──────────────────────────────────────────────────────────
function SummaryCards({ summary, onFilter, activeFilter }: any) {
  const cards = [
    { key: '',            label: 'Total',       value: summary?.total || 0,        color: '#1890ff' },
    { key: 'pendiente',   label: 'Recibidas',   value: summary?.pendiente || 0,    color: '#fa8c16' },
    { key: 'enviada_wms', label: 'En EIT',      value: summary?.enviada_wms || 0,  color: '#1890ff' },
    { key: 'procesada',   label: 'Procesadas',  value: summary?.procesada || 0,    color: '#52c41a' },
    { key: 'error_wms',   label: 'Con error',   value: summary?.error_wms || 0,    color: '#ff4d4f' },
    { key: 'facturada',   label: 'Facturadas',  value: summary?.facturada || 0,    color: '#722ed1' },
  ]

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
      {cards.map(c => (
        <Col key={c.key} xs={12} sm={8} md={4}>
          <Card
            hoverable
            size="small"
            onClick={() => onFilter(c.key)}
            style={{
              cursor: 'pointer',
              borderColor: activeFilter === c.key ? c.color : undefined,
              borderWidth: activeFilter === c.key ? 2 : 1,
            }}
          >
            <Statistic
              title={c.label}
              value={c.value}
              valueStyle={{ color: c.color, fontSize: 22 }}
            />
          </Card>
        </Col>
      ))}
    </Row>
  )
}

// ── Detalle expandible de una orden ──────────────────────────────────────────
function OrderDetail({ record }: { record: any }) {
  return (
    <div style={{ padding: '8px 16px' }}>
      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="Nosolicitud">{record.nosolicitud}</Descriptions.Item>
        <Descriptions.Item label="Región">{record.cliente_region}</Descriptions.Item>
        <Descriptions.Item label="Comuna">{record.cliente_comuna}</Descriptions.Item>
        <Descriptions.Item label="RUT cliente">{record.cliente_rut}</Descriptions.Item>
        {record.wms_ticket && (
          <Descriptions.Item label="Ticket EIT">
            <Text code>{record.wms_ticket}</Text>
          </Descriptions.Item>
        )}
        {record.wms_error && (
          <Descriptions.Item label="Error WMS" span={2}>
            <Text type="danger" style={{ fontSize: 12 }}>{record.wms_error}</Text>
          </Descriptions.Item>
        )}
      </Descriptions>
      <Table
        size="small"
        dataSource={record.productos}
        rowKey="cod_prod_vendedor"
        pagination={false}
        style={{ marginTop: 8 }}
      >
        <Table.Column title="Cód. vendedor" dataIndex="cod_prod_vendedor" width={160} render={(v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text>} />
        <Table.Column title="SKU comprador" dataIndex="cod_prod_comp" width={130} />
        <Table.Column title="Descripción" dataIndex="descripcion" />
        <Table.Column title="Cant." dataIndex="cantidad" width={60} align="center" />
        <Table.Column title="Precio neto" dataIndex="precio_neto" width={110} align="right"
          render={(v: number) => `$${v.toLocaleString('es-CL')}`} />
      </Table>
    </div>
  )
}

// ── Vista principal ───────────────────────────────────────────────────────────
export default function VeVIndex({ exposeSubMenu, setMainLoading }: any) {
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])

  const { data, loading } = useApi<any>('get', 'v3/sync/vev/orders', {
    requiredToken: true,
    onRequest: false,
  })

  useEffect(() => {
    if (!!exposeSubMenu) exposeSubMenu([])
  }, [exposeSubMenu])

  useEffect(() => {
    if (!loading) setMainLoading(false)
  }, [loading, setMainLoading])

  const orders: any[] = data?.orders || []
  const summary = data?.summary || {}

  const filtered = statusFilter
    ? orders.filter((o: any) => o.status === statusFilter)
    : orders

  const montoTotal = filtered.reduce((acc: number, o: any) => acc + o.monto_total, 0)

  const columns = [
    {
      title: 'OC',
      dataIndex: 'id_oc',
      key: 'id_oc',
      width: 90,
      render: (v: string) => <Text strong>#{v}</Text>,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v: string) => <StatusTag status={v} />,
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente_nombre',
      key: 'cliente_nombre',
      ellipsis: true,
    },
    {
      title: 'Monto neto',
      dataIndex: 'monto_total',
      key: 'monto_total',
      width: 120,
      align: 'right' as const,
      render: (v: number) => `$${v.toLocaleString('es-CL')}`,
    },
    {
      title: 'Emisión',
      dataIndex: 'fecha_emision',
      key: 'fecha_emision',
      width: 105,
    },
    {
      title: 'Compromiso',
      dataIndex: 'fecha_compromiso',
      key: 'fecha_compromiso',
      width: 105,
    },
    {
      title: 'Líneas',
      dataIndex: 'lineas',
      key: 'lineas',
      width: 65,
      align: 'center' as const,
    },
    {
      title: 'Ticket EIT',
      dataIndex: 'wms_ticket',
      key: 'wms_ticket',
      width: 110,
      render: (v: string) => v
        ? <Tooltip title="Ticket WMS EIT"><Text code style={{ fontSize: 11 }}>{v}</Text></Tooltip>
        : <Text type="secondary">—</Text>,
    },
  ]

  return (
    <div style={{ padding: '16px 24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space align="baseline">
            <h2 style={{ margin: 0 }}>Órdenes VeV</h2>
            <Badge count={filtered.length} style={{ backgroundColor: '#1890ff' }} />
          </Space>
          <div style={{ color: '#888', fontSize: 13, marginTop: 2 }}>
            Monto neto filtrado: <strong>${montoTotal.toLocaleString('es-CL')} CLP</strong>
          </div>
        </Col>
        <Col>
          <Select
            value={statusFilter}
            onChange={(v: string) => setStatusFilter(v)}
            style={{ width: 180 }}
            placeholder="Filtrar por estado"
          >
            <Option value="">Todos los estados</Option>
            {Object.entries(STATUS_CONFIG).map(([k, c]) => (
              <Option key={k} value={k}>
                <Tag color={c.color} style={{ margin: 0 }}>{c.label}</Tag>
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <SummaryCards
        summary={summary}
        activeFilter={statusFilter}
        onFilter={(k: string) => setStatusFilter(k)}
      />

      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="id_oc"
        loading={loading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} órdenes` }}
        expandable={{
          expandedRowKeys: expandedKeys,
          onExpand: (expanded, record) => {
            setExpandedKeys(expanded
              ? [...expandedKeys, record.id_oc]
              : expandedKeys.filter(k => k !== record.id_oc)
            )
          },
          expandedRowRender: (record: any) => <OrderDetail record={record} />,
        }}
        rowClassName={(record: any) => record.status === 'error_wms' ? 'row-error' : ''}
      />
    </div>
  )
}
